// src/App.jsx
import React, {Suspense, lazy, useState} from "react";
import {Provider, useDispatch, useSelector} from "react-redux";
import { store } from "./store/store";
import {BrowserRouter, Routes, Route, Navigate, useNavigate, useParams} from "react-router-dom";
import ProtectedRoute from "./component/permission/ProtectedRoute";
import MultiAuthGate from "./component/auth/MultiAuthGate";
import { PermissionProvider, usePermissions } from "./provider/PermissionProvider";
import PowerBIWrapper from "./component/permission/PowerBIWrapper";
import { ToastProvider } from "./provider/ToastProvider";
import Header from "./component/Header";
import { signOut } from "aws-amplify/auth";
import { FRONT_END_DOMAIN } from "./utils/LS-CONSTANT";
import {
    ADMIN_PERMISSION_EDIT,
    ADMIN_PERMISSION_VIEW,
    LS_DOCUMENTS_VIEW,
    ADMIN_FIRM_EDIT,
    LS_DOCUMENTTYPE_ALIAS_MAPPING,
    SEMANTIC_LAYER_ACCESS,
    CUSTOM_EXPORTS_ACCESS,
    DATASETS_ACCESS,
    CUSTOM_SQL_VIEW,
    TEMPLATES_ACCESS,
    RUN_HISTORY_ACCESS,
    ADMIN_CONTESTED_CASE_FORMS_EDIT
} from "./component/permission/permission-consts";
import {Box, IconButton, ThemeProvider, Tooltip, Typography} from "@mui/material";
import theme from "./theme/theme";
import LsPageLoader from "./core/LsPageLoader";
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {appUiAction} from "./store/slices/appUi";
import {LsSelect} from "./core";
import {POWERBI_REPORT_LIST} from "./component/home/Home";
import "./App.css";
import { Outlet } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "./component/error-boundary/ErrorFallback";
import logger from "./utils/logger";
import AppDownGate from "./component/app-down/AppDownGate";
import MSStatus from "./component/ms-status/MSStatus";

/* -----------------------------
   Lazy loaded pages
------------------------------ */

const Home = lazy(() => import("./component/home/Home"));
const UsersPage = lazy(() => import("./component/access-management/users/UsersPage"));
const PermissionsPage = lazy(() => import("./component/access-management/permissions/PermissionsPage"));
const RolesPage = lazy(() => import("./component/access-management/roles/RolesPage"));
const DocumentsPage = lazy(() => import("./component/documents/all-documents/DocumentsPage"));
const AccountPage = lazy(() => import("./component/account/AccountPage"));
const FirmsPage = lazy(() => import("./component/firms/FirmsPage"));
const SemanticAdmin = lazy(() => import("./component/custom-reports/semantic-admin"));
const Dataset = lazy(() => import("./component/custom-reports/dataset/components"));
const ContestedCaseForms = lazy(() => import("./component/contested-case-forms/ContestCaseFormAdmin"));
const AllSubmissions = lazy(() => import("./component/contested-case-forms/all-submissions/AllSubmissions"));
const RuntimeWizardLoader = lazy(() => import("./component/contested-case-forms/loaders/RuntimeWizardLoader"));
const ProfilePage = lazy(() => import("./component/profile/ProfilePage"));

const drawerWidth = 0;

/* -----------------------------
   Layout
------------------------------ */

const Layout = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const { zenView } = useSelector(state => state.appUi)

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const handleSignOut = async () => {
        try {
            document.cookie =
                "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure";
            await signOut();
            window.location.href = `https://${FRONT_END_DOMAIN}`;
        } catch (error) {
            logger.error("Error during sign out:", error);
        }
    };

    return (
        <Box sx={{ display: "flex", overflow: 'hidden', width: '100vw' }}>
            <Header
                onSignOut={handleSignOut}
                open={sidebarOpen}
                onToggle={() => setSidebarOpen(p => !p)}
            />

            {/* Fixed stripe to cover the 42px gap in zen mode */}
            {zenView && (
                <Box sx={(theme) => ({
                    position: 'fixed',
                    top: 0,
                    left: drawerWidth,
                    right: 0,
                    height: '40px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                })}>
                    {/* Left */}
                    {(() => {
                        const current = POWERBI_REPORT_LIST.find(r => r.url === window.location.pathname);
                        const CurrentIcon = current?.Icon;
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase', userSelect: 'none' }}>
                                    Zen View
                                </Typography>
                                <Box sx={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.15)', mx: 1 }} />

                                {/* Icon of currently selected dashboard */}
                                {CurrentIcon && <CurrentIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />}

                                {/* Dashboard Switcher */}
                                <LsSelect
                                    value={window.location.pathname}
                                    onChange={(e) => navigate(e.target.value)}
                                    options={POWERBI_REPORT_LIST.map(r => ({ label: r.title, value: r.url }))}
                                    size="small"
                                    sx={{
                                        minWidth: 240,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                            color: '#fff',
                                            fontSize: '0.8rem',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                        },
                                        '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
                                        '& .MuiInputLabel-root': { display: 'none' },
                                    }}
                                />

                            </Box>
                        );
                    })()}

                    {/* Right — Exit button */}
                    <Tooltip title="Exit Zen View">
                        <IconButton
                            size="small"
                            onClick={() => dispatch(appUiAction.setZenView(false))}
                            sx={{
                                color: 'rgba(255,255,255,0.6)',
                                borderRadius: '6px',
                                px: 1,
                                gap: 0.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    color: '#fff',
                                }
                            }}
                        >
                            <FullscreenExitIcon sx={{ fontSize: 15 }} />
                            <Typography sx={{ fontSize: '0.72rem', color: 'inherit' }}>Exit</Typography>
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <Box
                component="main"
                sx={{
                    marginLeft: 0,
                    width: `calc(100vw - ${drawerWidth}px)`,
                    marginTop: zenView ? '0' : "64px",
                    transition: "margin-left 0.3s, width 0.3s",
                    minHeight: "calc(100vh - 64px)",
                    backgroundColor: "#f8fafc",
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

function App() {
    return (
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <ToastProvider>
                    <PermissionProvider>
                        <AppDownGate>
                            <BrowserRouter>
                                <MultiAuthGate>
                                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                                        <Suspense fallback={<LsPageLoader/>}>
                                            <Routes>
                                                <Route path="/" element={<Layout/>}>

                                                    {/* Default → /dashboards */}
                                                    <Route
                                                        index
                                                        element={<Navigate to="/dashboards" replace/>}
                                                    />

                                                    {/* /home → /dashboards */}
                                                    <Route
                                                        path="/home"
                                                        element={<Navigate to="/dashboards" replace/>}
                                                    />

                                                    <Route
                                                        path="/ms/status"
                                                        element={
                                                                <MSStatus />
                                                        }
                                                    />

                                                    {/* Dashboards */}
                                                    <Route
                                                        path="/dashboards"
                                                        element={
                                                            <ProtectedRoute>
                                                                <Home/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/powerbi/:report"
                                                        element={<PowerBIWrapper/>}
                                                    />

                                                    <Route
                                                        path="/access-control/users"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[
                                                                    ADMIN_PERMISSION_VIEW,
                                                                    ADMIN_PERMISSION_EDIT,
                                                                ]}
                                                            >
                                                                <UsersPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/access-control/permissions"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[
                                                                    ADMIN_PERMISSION_VIEW,
                                                                    ADMIN_PERMISSION_EDIT,
                                                                ]}
                                                            >
                                                                <PermissionsPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/access-control/roles"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[
                                                                    ADMIN_PERMISSION_VIEW,
                                                                    ADMIN_PERMISSION_EDIT,
                                                                ]}
                                                            >
                                                                <RolesPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route path="/my/profile" element={<ProfilePage />} />

                                                    <Route
                                                        path="/documents/*"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                            >
                                                                <DocumentsPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/documents/search"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                            >
                                                                <DocumentsPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/naming-issue-files"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                            >
                                                                <DocumentsPage />
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/doctype-alias-mapping/*"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[LS_DOCUMENTTYPE_ALIAS_MAPPING]}
                                                            >
                                                                <DocumentsPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/archived-documents/*"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                            >
                                                                <DocumentsPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/firms/*"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[ADMIN_FIRM_EDIT]}
                                                            >
                                                                <FirmsPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/accounts"
                                                        element={
                                                            <ProtectedRoute
                                                                anyPermissions={[LS_DOCUMENTS_VIEW]}
                                                            >
                                                                <AccountPage/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    {/* Semantic Layer */}
                                                    <Route
                                                        path="/semantic-layer"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[SEMANTIC_LAYER_ACCESS]}>
                                                                <Navigate to="/semantic-layer/entities" replace/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/semantic-layer/entities"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[SEMANTIC_LAYER_ACCESS]}>
                                                                <SemanticAdmin/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/semantic-layer/relationships"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[SEMANTIC_LAYER_ACCESS]}>
                                                                <SemanticAdmin/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/semantic-layer/schema-explorer"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[SEMANTIC_LAYER_ACCESS]}>
                                                                <SemanticAdmin/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    {/* /custom-reports → redirect to datasets home */}
                                                    <Route
                                                        path="/custom-reports"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[CUSTOM_EXPORTS_ACCESS]}>
                                                                <Navigate to="/custom-reports/datasets" replace/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    {/* Datasets */}
                                                    <Route
                                                        path="/custom-reports/datasets"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="datasets-home"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/draft"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="datasets-draft"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/ready"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="datasets-ready"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="datasets-detail"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    {/* Dataset wizard steps */}
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId/header-setup"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="wizard"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId/map-headers"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="wizard"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId/sql-preview"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[CUSTOM_SQL_VIEW]}>
                                                                <Dataset key="wizard"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId/output-preview"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[CUSTOM_SQL_VIEW]}>
                                                                <Dataset key="wizard"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId/export-format-settings"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="wizard"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/datasets/:datasetId/export-triggers"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[DATASETS_ACCESS]}>
                                                                <Dataset key="wizard"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    {/* Templates */}
                                                    <Route
                                                        path="/custom-reports/templates/all"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[TEMPLATES_ACCESS]}>
                                                                <Dataset key="templates-all"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/templates/templates"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[TEMPLATES_ACCESS]}>
                                                                <Dataset key="templates-templates"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/templates/private"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[TEMPLATES_ACCESS]}>
                                                                <Dataset key="templates-private"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />



                                                    <Route
                                                        path="/custom-reports/fwf-templates/all"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[TEMPLATES_ACCESS]}>
                                                                <Dataset key="fwf-templates-all" />
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/fwf-templates/templates"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[TEMPLATES_ACCESS]}>
                                                                <Dataset key="fwf-templates-templates"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/fwf-templates/private"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[TEMPLATES_ACCESS]}>
                                                                <Dataset key="fwf-templates-private"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    {/* Run History */}
                                                    <Route
                                                        path="/custom-reports/run-history"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[RUN_HISTORY_ACCESS]}>
                                                                <Dataset key="run-history"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/custom-reports/run-history/:datasetId"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[RUN_HISTORY_ACCESS]}>
                                                                <Dataset key="run-history"/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/contested-case-forms"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[ADMIN_CONTESTED_CASE_FORMS_EDIT]}>
                                                                <ContestedCaseForms/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/contested-case-forms/submissions"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[ADMIN_CONTESTED_CASE_FORMS_EDIT]}>
                                                                <AllSubmissions/>
                                                            </ProtectedRoute>
                                                        }
                                                    />

                                                    <Route
                                                        path="/wizard/preview/:wizardId"
                                                        element={
                                                            <ProtectedRoute anyPermissions={[ADMIN_CONTESTED_CASE_FORMS_EDIT]}>
                                                                <RuntimeWizardLoader/>
                                                            </ProtectedRoute>}
                                                    />
                                                </Route>

                                                <Route path="*" element={<Navigate to="/dashboards" replace/>}/>
                                            </Routes>
                                        </Suspense>
                                    </ErrorBoundary>
                                </MultiAuthGate>
                            </BrowserRouter>
                        </AppDownGate>
                    </PermissionProvider>
                </ToastProvider>
            </ThemeProvider>
        </Provider>
    );
}

export default App;